<template>
  <div
    v-if="tagsToDisplay.length"
    class="SearchQuickFilters"
  >
    <div
      v-for="tag in tagsToDisplay"
      :key="tag.queryParam"
      class="SearchQuickFilters-tag"
    >
      <div class="Outline">
        <strong>{{ tag.label }} </strong>
        <span
          v-if="tag.value"
          class="Tag Tag--grey"
          @click="removeSingleFilter({ value: tag.id, queryParam: tag.queryParam })"
        >
          {{ tag.value }}
          <i class="far fa-times Tag-closeIcon" />
        </span>

        <span
          v-else-if="tag.labelOnly"
          class="Tag"
          @click="removeSingleFilter({ value: tag.id, queryParam: tag.queryParam })"
        >
          <i class="far fa-times Tag-closeIcon" />
        </span>

        <span
          v-if="tag.children"
          class="fa fa-angle-right Tag-iconRight"
        />

        <span
          v-if="tag.children"
          class="Tag Tag--grey"
          @click="removeSingleFilter({ value: tag.children.id, queryParam: tag.children.queryParam })"
        >
          {{ tag.children.value }}
          <i class="far fa-times Tag-closeIcon" />
        </span>
      </div>
    </div>
    <a
      href="#"
      @click.prevent="resetQuickQueryParams"
    >
      Clear filters
    </a>
  </div>
</template>

<script>
import {
  mapState, mapMutations, mapGetters, mapActions,
} from 'vuex';
import { axios } from '@/axios';
import _ from '@/lodash';
import EventBus from '@/event-bus';

export default {
  data() {
    return {};
  },

  computed: {
    ...mapState('search', {
      query: (state) => state.query,
      filters: (state) => state.filters,
      currentSortOrder: (state) => state.query.od,
      currentShowParam: (state) => state.query.show,
      defaultUrls: (state) => state.defaultUrls,
      locationsMeta: (state) => state.locationsMeta,
    }),
    ...mapGetters('search', {
      getFormattedRouteParams: 'getFormattedRouteParams',
    }),

    tagsToDisplay() {
      return [
        this.generateTextTag({ param: 's', label: 'Text Search:' }),
        this.generateTedTag(),
        this.generateJobTypeTag(),
        this.generateCompanyIndustryTag(),
        this.generateLocationTag(),
        this.generateNestedTags({
          queryKeyParent: 'disciplines_parent',
          queryKeyChild: 'discipline',
          filterValues: 'jobRoles',
        }),
      ].filter((tag) => { return tag !== false; });
    },
  },

  methods: {
    ...mapActions('search', {
      getAjaxResults: 'getAjaxResults',
    }),
    ...mapMutations('search', {
      resetQueryParams: 'resetQueryParams',
      removeQuickTag: 'removeQuickTag',
      replaceQuery: 'replaceQuery',
      updateSearchUrl: 'updateSearchUrl',
      pauseQueryWatcher: 'pauseQueryWatcher',
      unPauseQueryWatcher: 'unPauseQueryWatcher',
      setLoadingState: 'setLoadingState',
    }),
    /**
     * This has changed since introduced the simplified and query string based search url.
     * As a reminder, the state.query no longer "rules" over the request that is submitted to the backend.
     * This is why when a single filter is removed, the app needs to ask the backend to generate the new search url.
     *
     * @param value
     * @param queryParam
     */
    removeSingleFilter({ value, queryParam }) {
      // So the updateSearchUrl() call a couple of lines down under will emit the disable-all-filter...
      // However, for this particular scenario we need to disable the filters sooner, so here we go...
      EventBus.$emit('bus:disable-all-filters-while-search-request-is-being-processed');

      this.setLoadingState({ loadingState: true });
      this.pauseQueryWatcher();

      this.removeQuickTag({ value, queryParam });

      // Needed to communicate with location auto-suggest component to remove the value from there
      if (queryParam === 'location') {
        EventBus.$emit('bus:location-quick-filter:remove');
      }

      //  Given that a search url has 2 variations (simplified and query string) we need to enquiry the backend to resolve it
      axios.post(
        '/resolve-url-for-search-quick-filter-deletion',
        { queryParams: this.getFormattedRouteParams({ routeParams: _.cloneDeep(this.query) }) },
      )
        .then((response) => {
          this.updateSearchUrl(response.data.data.url);

          // Got the search url, so let's submit the search
          this.getAjaxResults().catch((error) => {
            if (error.message === 'cancelled-request') {
              console.log('request cancelled.');
              return;
            }
            this.$bus.$emit('bus:base-alert', {
              type: 'Error',
              message: this.$lang.alert.error.message,
            });
          }).finally(() => {
            this.setLoadingState({ loadingState: false });
            this.unPauseQueryWatcher();
          });
        })
        .finally(() => {
          this.unPauseQueryWatcher();
        });
    },
    resetQuickQueryParams() {
      this.updateSearchUrl(this.defaultUrls[this.currentShowParam]);
      this.resetQueryParams({
        query: {
          od: this.currentSortOrder,
          show: this.currentShowParam,
        },
      });
    },

    lookupTagName({ filters, lookupValue }) {
      if (filters.length === 0) {
        return false;
      }

      const tag = filters.find((filter) => {
        // Note: Blame @bruno
          return filter.key == lookupValue; // eslint-disable-line
      });

      if (!tag) {
        return false;
      }

      return {
        name: tag.name,
        key: tag.key,
      };
    },

    generateTextTag({ param, label }) {
      if (!this.query[param] || !this.query[param].length) {
        return false;
      }

      return {
        label,
        value: this.query[param],
        queryParam: param,
      };
    },

    generateTedTag() {
      if (this.query.best_apprenticeship_employers.length === 0) {
        return false;
      }

      return {
        label: 'Best Apprenticeship Employers',
        value: null,
        labelOnly: true,
        queryParam: 'best_apprenticeship_employers',
        id: 'best_apprenticeship_employers',
      };
    },

    generateLocationTag() {
      if (this.query.location.length > 0) {
        return {
          label: 'Location:',
          value: this.locationsMeta.locations.length > 0 ? this.locationsMeta.locations[0].formatted_address : this.query.location,
          queryParam: 'location',
        };
      }

      return false;
    },

    generateJobTypeTag() {
      if (!Array.isArray(this.filters.jobTypes) || this.filters.jobTypes.length === 0) {
        return false;
      }

      if (this.query.programme.length === 1) {
        const tagLookup = this.lookupTagName({
          filters: this.filters.jobTypes,
          lookupValue: this.query.programme[0],
        });

        if (!tagLookup) {
          return false;
        }

        return {
          label: 'Job Type:',
          value: tagLookup.name,
          queryParam: 'programme',
          id: tagLookup.key,
        };
      }

      if (this.query.programme.length > 1) {
        return {
          label: 'Job Types:',
          value: `Multiple Filters (${this.query.programme.length})`,
          queryParam: 'programme',
          id: 'multiple',
        };
      }

      return false;
    },

    generateCompanyIndustryTag() {
      if (!Array.isArray(this.filters.companyIndustries) || this.filters.companyIndustries.length === 0) {
        return false;
      }

      if (this.query.discipline.length === 1) {
        const tagLookup = this.lookupTagName({
          filters: this.filters.companyIndustries,
          lookupValue: this.query.discipline[0],
        });

        if (!tagLookup) {
          return false;
        }

        return {
          label: 'Industry:',
          value: tagLookup.name,
          queryParam: 'discipline',
          id: tagLookup.key,
        };
      }

      if (this.query.discipline.length > 1) {
        return {
          label: 'Industries:',
          value: `Multiple Filters (${this.query.discipline.length})`,
          queryParam: 'discipline',
          id: 'multiple',
        };
      }

      return false;
    },

    generateNestedTags({ queryKeyParent, queryKeyChild, filterValues }) {
      if (this.query[queryKeyParent] === false || this.filters[filterValues].groups.length === 0) { return false; }

      const tagLookup = this.lookupTagName({
        filters: this.filters[filterValues].groups,
        lookupValue: this.query[queryKeyParent],
      });

      if (!tagLookup) {
        return false;
      }

      let children = '';

      if (this.query[queryKeyChild].length === 1) {
        const childTagLookup = this.lookupTagName({
          filters: this.filters[filterValues].values,
          lookupValue: this.query[queryKeyChild][0],
        });

        children = {
          value: `${childTagLookup.name}`,
          id: this.query[queryKeyChild][0],
          queryParam: queryKeyChild,
        };
      } else if (this.query[queryKeyChild].length > 1) {
        children = {
          value: `Multiple Filters (${this.query[queryKeyChild].length})`,
          id: 'multiple',
          queryParam: queryKeyChild,
        };
      }

      return {
        label: 'Job Role Category:',
        value: tagLookup.name,
        queryParam: queryKeyParent,
        id: tagLookup.key,
        children,
      };
    },

    initialCapWord(word) {
      return word.replace(/(^[a-zA-Z]{1})/, (firstChar) => {
        return firstChar.toUpperCase();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "../../../../scss/abstracts/abstracts";

  .SearchQuickFilters {
    display: flex;
    flex-wrap: wrap;
    padding-left: spacing();
    padding-right: spacing();
    margin-top: spacing();
  }

  .SearchQuickFilters-tag {
    margin-right: 10px;
    /*cursor: pointer;*/
    margin-bottom: 10px;
    width: 100%;

    .Tag {
      white-space: normal;
      display: flex;
      align-items: center;
      height: auto;
    }

    .Tag-closeIcon {
      margin-left: 3px;
    }
    strong {
      margin-right: .3rem;
      width: 100%;
    }
    @include respond-to(sm) {
      width: auto;
      strong {
        width: auto;
      }
    }
  }

  .Outline {
    padding: 2px 2px 2px 6px;
    border: 1px solid color('grey', 'light');
    background: #fff;
    font-size: 16px;
    border-radius: radius();
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  .Tag {
    margin-bottom: 0;
    cursor:pointer;
  }
  .Tag-closeIcon {

  }
  .Tag--grey {
    background-color: #f9f6e6;
  }
</style>
